import { Static, Type } from "@fastify/type-provider-typebox";

export enum ErrorCode {
  // manually add a new enum value that does not overlap with others as you discover a new identifiable error kind.
  // don't add a value for kinds that aren't unique or identifiable (can mean multiple things), or for things that
  // overlap with http status codes.

  // @TODO(nick) remove, these next 5 aren't specific enough to really make sense (refer to above,
  // error codes must identify unique situations and not overlap with http status codes,
  // all of these overlap with http status codes and aren't unique. Authentication is 401,
  // Authorization is 403, Bad Parameter is 400, Server Quota is 503, then 'not_elligible' 
  // isn't really specific enough + it's used in two different places for two different errors)
  AUTHENTICATION = "BX-001",
  AUTHORIZATION = "BX-002",
  BAD_PARAMETER = "BX-003",
  SERVER_QUOTA_EXCEEDED = "BX-004",
  NOT_ELIGIBLE = "BX-005",

  SQS_SEND_ERROR = "BX-007",
  SQS_RECEIVE_ERROR = "BX-008",
  FILEDOWNLOAD_ERROR = "BX-009",
  DUPLICATE_ITEM = "BX-010",
  MISSING_PARAMETER = "BX-011",
  LISTING_RESTRICTION_APPROVAL_REQUIRED = "BX-012",
  LISTING_RESTRICTION_ASIN_NOT_FOUND = "BX-013",
  LISTING_RESTRICTION_NOT_ELIGIBLE = "BX-014",
  NOT_SUBSCRIBED = "BX-015",
  SELLER_CENTRAL_NOT_CONNECTED = "BX-016",
  SELLER_ID_MISMATCH = "BX-017",
  SELLER_ID_IN_USE = "BX-018",
  TOKEN_EXPIRED = "BX-019",
  FETCH_REQUEST_NETWORK_ERROR = "BX-NE",
  FETCH_REQUEST_PARSE_JSON = "BX-PJ",
  SIGNUP_ACCOUNT_EMAIL_ALREADY_EXISTS = "BX-020",
  REQUIRES_EMAIL_VERIFICATION = "BX-021",
  CHANGE_EMAIL_EMAIL_ALREADY_EXISTS = "BX-022",
  FBA_2D_BARCODES_ARE_NOT_ENABLED = "BX-25",
  MISSING_RESOURCE = "BX-30",

  POLLING_TIMEOUT = "BX-33",
  FBA_PREP_CATEGORY_MISSING = "BX-35",
  
  SKU_DOES_NOT_REQUIRE_PREP_OWNER_BUT_SELLER_OR_AMAZON_WAS_ASSIGNED = "BX-38",
  POLLING_WAS_ABORTED_BECAUSE_OF_USER_NAVIGATE = "BX-39",
  POLLING_WAS_ABORTED_BECAUSE_OF_EXCEEDING_MAX_ITERATIONS = "BX-40",
  ADD_TO_BATCH_ITEM_MISSING_EXPIRATION_DATE = "BX-41",
  ADD_TO_BATCH_LISTING_DOESNT_EXIST_OR_DELETED = "BX-42",

  ADMIN_FAILED_TO_FETCH_MIRROR_JWT = "BX-43",
  ADMIN_FAILED_TO_FETCH_BATCHES = "BX-44",

  SIGNUP_OTP_EXPIRED = "BX-45",

  LTL_BOX_QUOTA_EXCEEDED = "BX-LTL-001",
  GENERIC = "BX-GN", // @TODO remove
}

export enum ErrorSource {
  SPAPI = "SPAPI",
  AWS = "AWS",
  RECURLY = "RECURLY",
  ACTIVE_CAMPAIGN = "ACTIVE_CAMPAIGN",
  GOAURA = "GOAURA",
  BOXEM = "BOXEM",
  KEEPA = "KEEPA",
}

export function errorCodeIsGeneric(errorCode: ErrorCode): boolean {
  if (!errorCode) return true;
  // there are a couple specific error codes that are shared enough to consider them 'generic'.
  // we should avoid having too many of these - in general error codes should be as specific as
  // possible.
  switch (errorCode) {
    case ErrorCode.NOT_SUBSCRIBED:
    case ErrorCode.SELLER_CENTRAL_NOT_CONNECTED:
    // case ErrorCode.POLLING_WAS_ABORTED_BECAUSE_OF_USER_NAVIGATE: // this one we just want to be a silent 'failure'.
    case ErrorCode.POLLING_WAS_ABORTED_BECAUSE_OF_EXCEEDING_MAX_ITERATIONS:
    case ErrorCode.FETCH_REQUEST_NETWORK_ERROR:
    case ErrorCode.FETCH_REQUEST_PARSE_JSON:
    return true;
  }
  return false;
}

export const ErrorAction = Type.Object({
  link: Type.String(),
  description: Type.Optional(Type.String()),
  details: Type.Optional(Type.Any()),
});
export type ErrorAction = Static<typeof ErrorAction>;

export const BxError = Type.Object({
  type: Type.Optional(Type.Enum(ErrorCode)), // omitted in the case of 'generic' or unknown errors.
  details: Type.Optional(Type.Any()), // if 'type' is set, we may have some additional info like 'which shipment is this error about', etc.
  actions: Type.Optional(Type.Array(ErrorAction)), // array of clickable links in the circumstance the user needs/wants to visit a third-party site to rectify the error.
  
  // set the default to BOXEM. If it's other sources, set it individually
  source: Type.Optional(Type.Enum(ErrorSource)),
  dev: Type.Optional(Type.String()),
  code: Type.Optional(Type.Any()), // only set if 'type' is not set. a third-party error code.
  message: Type.Optional(Type.String()), // only set if 'type' is not set. a third-party error message.
});
export type BxError = Static<typeof BxError>;

export const BxErrors = Type.Array(BxError);
export type BxErrors = Static<typeof BxErrors>;

// https://developer-docs.amazon.com/sp-api/docs/fbainboundeligibility-api-v1-reference#error
export interface SPAPIError {
  code: string;
  message?: string;
  details?: string;
}

export enum IneligibilityReasonList {
  // these are all error codes from this table: https://developer-docs.amazon.com/sp-api/docs/fbainboundeligibility-api-v1-reference#ineligibilityreasonlist
  // which we can use to uniquely identify inbound item eligibility errors.
  FBA_INB_0004 = "FBA_INB_0004",
  FBA_INB_0006 = "FBA_INB_0006",
  FBA_INB_0007 = "FBA_INB_0007",
  FBA_INB_0008 = "FBA_INB_0008",
  FBA_INB_0009 = "FBA_INB_0009",
  FBA_INB_0010 = "FBA_INB_0010",
  FBA_INB_0011 = "FBA_INB_0011",
  FBA_INB_0012 = "FBA_INB_0012",
  FBA_INB_0013 = "FBA_INB_0013",
  FBA_INB_0014 = "FBA_INB_0014",
  FBA_INB_0015 = "FBA_INB_0015",
  FBA_INB_0016 = "FBA_INB_0016",
  FBA_INB_0017 = "FBA_INB_0017",
  FBA_INB_0018 = "FBA_INB_0018",
  FBA_INB_0019 = "FBA_INB_0019",
  FBA_INB_0034 = "FBA_INB_0034",
  FBA_INB_0035 = "FBA_INB_0035",
  FBA_INB_0036 = "FBA_INB_0036",
  FBA_INB_0037 = "FBA_INB_0037",
  FBA_INB_0038 = "FBA_INB_0038",
  FBA_INB_0050 = "FBA_INB_0050",
  FBA_INB_0051 = "FBA_INB_0051",
  FBA_INB_0053 = "FBA_INB_0053",
  FBA_INB_0055 = "FBA_INB_0055",
  FBA_INB_0056 = "FBA_INB_0056",
  FBA_INB_0059 = "FBA_INB_0059",
  FBA_INB_0065 = "FBA_INB_0065",
  FBA_INB_0066 = "FBA_INB_0066",
  FBA_INB_0067 = "FBA_INB_0067",
  FBA_INB_0068 = "FBA_INB_0068",
  FBA_INB_0095 = "FBA_INB_0095",
  FBA_INB_0097 = "FBA_INB_0097",
  FBA_INB_0098 = "FBA_INB_0098",
  FBA_INB_0099 = "FBA_INB_0099",
  FBA_INB_0100 = "FBA_INB_0100",
  FBA_INB_0103 = "FBA_INB_0103",
  FBA_INB_0104 = "FBA_INB_0104",
  FBA_INB_0197 = "FBA_INB_0197",
  UNKNOWN_INB_ERROR_CODE = "FBA_INB_UNKNOWN",
}

export enum FbaErrors {
  FBA_2D_BARCODES_NOT_ENABLED = "Unsupported option intendedBoxContentsSource =2D_BARCODE provided for Operation=CreateInboundShipment. This option is only supported if you’ve enrolled in the FBA 2D Barcode program.",
}
